import React, { useEffect, useState, useRef } from 'react';
import { ForgeLinearProgress } from '@tylertech/forge-react';
import { I18n } from 'common/i18n';
import AceEditor from 'react-ace';
import { ValidationElements } from './ValidationElements';
import { SetupHighlightingListeners } from './customHooks/useAutocompleteHighlighting';
import {
  createCustomCompleter,
  setupAutocomplete,
  setupForgeDialogIntegration,
} from './utils/autocompleteUtils';
import './FormulaEditor.scss';

export interface FormulaEditorProps {
  /** Array of available values to appear in the autocomplete suggestions */
  autocompleteValues: string[];
  /** Current formula string value */
  currentFormula: string;
  /** Callback function to update the formula when user types */
  updateFormula: (formula: string) => void;
  /** Async function to validate the formula */
  validateFormula: (formula: string) => Promise<boolean>;
  /**
   * Required if using FormulaEditor in a forge dialog.
   * This is used to correctly position the autocomplete popup above the dialog.
   * @warning Ensure the dom element with this id exists when the editor is mounted.
   */
  forgeDialogId?: string;
  setupHighlightingListeners: SetupHighlightingListeners;
}

const t = (key: string) => I18n.translate(key, { scope: 'shared.components.formula_editor' });

/**
 * FormulaEditor component provides a Ace editor with validation and autocomplete functionality.
 * Features include:
 * - Real-time syntax highlighting and autocomplete
 * - Debounced validation (1 second delay after user stops typing)
 * - Visual feedback for validation status (success/error states)
 */
export const FormulaEditor: React.FC<FormulaEditorProps> = ({
  autocompleteValues,
  currentFormula,
  updateFormula,
  validateFormula,
  forgeDialogId,
  setupHighlightingListeners,
}) => {
  const [formulaValid, setFormulaValid] = useState<boolean | null>(null);
  const [validationRunning, setValidationRunning] = useState<boolean>(false);
  const autocompleteValuesRef = useRef<string[]>([]);

  // Track the previous autocomplete values to detect when the user switches row groups
  const prevAutocompleteValues = useRef<string[]>([]);
  // Use a ref to track if we should skip the next validation cycle
  const skipValidation = useRef(true);

  // Keep ref in sync with prop
  useEffect(() => {
    // If the autocomplete values have changed, it means the user switched row groups.
    // We set skipValidation to true so the next formula change (the unwrap) is ignored.
    if (JSON.stringify(prevAutocompleteValues.current) !== JSON.stringify(autocompleteValues)) {
      skipValidation.current = true;
      prevAutocompleteValues.current = autocompleteValues;
    }
    autocompleteValuesRef.current = autocompleteValues;
  }, [autocompleteValues]);

  // Validate formula with debounce - only runs 1 second after user stops typing
  useEffect(() => {
    // If skipValidation is true, this change came from a modal load or a row group switch.
    // We reset the flag and exit early to avoid triggering the async validation.
    if (skipValidation.current) {
      skipValidation.current = false;
      return;
    }

    // Reset validation states immediately when user types
    setValidationRunning(false);
    if (currentFormula !== '') {
      setFormulaValid(null);
    }

    // if there is no formula clear validation
    if (currentFormula === '') {
      setFormulaValid(null);
    } else {
      // add one second debounce before validating
      const timeoutId = setTimeout(() => {
        setValidationRunning(true);
        setFormulaValid(null);

        validateFormula(currentFormula).then((isValid) => {
          setFormulaValid(isValid);
          setValidationRunning(false);
        });
      }, 1000);

      // Cleanup timeout if currentCustomTotal changes before timeout completes
      // This cancels the validation if user starts typing again
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [currentFormula]);

  const bootstrapEditor = (editor: any) => {
    editor.setHighlightActiveLine(false);
    editor.setOptions({
      fontFamily: 'courier',
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
    });
    editor.container.style.lineHeight = '1.5';

    // Setup autocomplete
    const customCompleter = createCustomCompleter(autocompleteValuesRef);
    setupAutocomplete(editor, customCompleter);

    // Setup highlighting
    setupHighlightingListeners(editor, autocompleteValuesRef);

    // Setup forge dialog
    if (forgeDialogId) {
      setupForgeDialogIntegration(editor);
    }
  };

  return (
    <div className="custom-totals-body" data-testid="formula-editor">
      <div className="custom-totals-border ">
        <div className="custom-totals-soql-editor">
          <AceEditor
            theme="chrome"
            mode="text"
            width="100%"
            height="130px"
            fontSize={14}
            tabSize={2}
            showGutter={false}
            placeholder={t('formula_placeholder')}
            editorProps={{ $blockScrolling: true }}
            enableBasicAutocompletion={true}
            enableLiveAutocompletion={true}
            value={currentFormula}
            onChange={(newFormula: string) => {
              updateFormula(newFormula);
            }}
            onLoad={bootstrapEditor}
          />
        </div>
        {validationRunning ? <ForgeLinearProgress /> : <div className="progress-bar-placeholder" />}
      </div>
      <ValidationElements formulaValid={formulaValid} />
    </div>
  );
};
